import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Deep-linking into a list screen: /screen?open=<id>[&q=<term>]
 *
 * The header search links straight at a record, so the screen it lands on has to
 * surface THAT record — open its detail if the screen has one, otherwise filter
 * the list down to it — rather than dumping the user on an unfiltered list.
 *
 *   const { openId, q, clear } = useDeepLink();
 *
 * `clear` strips the params (replace, so Back still works) once consumed, which
 * stops a refresh or a later navigation from re-opening the same record.
 */
export function useDeepLink() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const openId = params.get('open');
  const q = params.get('q');
  const clear = useCallback(
    () => navigate(location.pathname, { replace: true }),
    [navigate, location.pathname],
  );
  return { openId, q, clear };
}

/**
 * The common case: once `rows` have loaded, find the deep-linked one and hand it
 * to `onOpen`. No-ops until there's both an id and data to match it against.
 *
 *   useOpenFromLink(staff, (s, id) => String(s.id) === id, setDetail);
 */
export function useOpenFromLink(rows, matches, onOpen) {
  const { openId, clear } = useDeepLink();
  useEffect(() => {
    if (!openId || !Array.isArray(rows) || !rows.length) return;
    const hit = rows.find((r) => matches(r, openId));
    if (hit) { onOpen(hit); clear(); }
    // matches/onOpen are inline lambdas at every call site — depending on them
    // would re-run this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, rows]);
}

/**
 * For screens with no detail view: seed the screen's own search box from `?q=`
 * so the list lands filtered to the record that was clicked.
 */
export function useQueryFromLink(setQuery) {
  const { q, clear } = useDeepLink();
  useEffect(() => {
    if (!q) return;
    setQuery(q);
    clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
}

export default useDeepLink;
